import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { Router, RouterModule } from '@angular/router';
import currency from 'currency.js';
import { firstValueFrom, Subject, Subscription } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { DealService } from '../../modules/advance-application/services/deal.service';
import { PromisedPaymentService } from '../../modules/advance-application/services/promised-payment.service';
import { ProductEnum } from '../../modules/authentication/models/product.enum';
import { User } from '../../modules/authentication/models/v1-api.interface';
import { AuthenticationService } from '../../modules/authentication/services/authentication.service';
import { LocalisePipe } from '../../modules/pipes/localise.pipe';
import { ReferFriendComponent } from '../../modules/shared/components/refer-friend/refer-friend.component';
import { AccountService } from '../../modules/shared/services/account.service';
import { BrokerService } from '../../modules/shared/services/broker.service';
import { TransactionService } from '../../modules/shared/services/transaction.service';
import { IPartner } from '../../pages/dashboard/models/partner';
import { ProgressLoadingPageService } from '../../services/progress-loading-page.service';
import { ThemesService } from '../../services/themes.service';

@Component({
  selector: 'app-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatProgressBarModule,
    MatDialogModule,
    LocalisePipe,
  ],
})
export class NavigationComponent implements OnInit, OnDestroy {
  public showMenu = false;
  public isAuthenticated = false;
  protected loginSuccessActionSubscriber = new Subscription();
  public user: User | null = null;
  public availableBalance = currency(0);
  public balance = currency(0);
  public hasTaxDocuments?: boolean;
  public partners: IPartner[];
  public readonly productEnum = ProductEnum;
  private unsubscribe$ = new Subject<boolean>();

  constructor(
    private accountService: AccountService,
    private transactionService: TransactionService,
    private authenticationService: AuthenticationService,
    private brokerService: BrokerService,
    private matDialog: MatDialog,
    public router: Router,
    private dealService: DealService,
    private promisedPaymentService: PromisedPaymentService,
    public dialog: MatDialog,
    public progressLoadingPageService: ProgressLoadingPageService,
    public themesService: ThemesService,
  ) {
    // redirect the user if they're already authenticated.
    this.authenticationService.isAuth.subscribe((isAuthenticated) => {
      this.isAuthenticated = isAuthenticated;

      if (this.isAuthenticated) {
        this.loadBalance();
        this.loadPartners();
      }
    });
  }

  ngOnInit(): void {
    this.authenticationService.profile
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(async (user) => {
        this.user = user;

        if (typeof this.hasTaxDocuments === 'boolean' || !user.id) {
          return;
        }

        const list = await this.promisedPaymentService
          .getStatementsByYears()
          .toPromise();
        this.hasTaxDocuments = !!list?.length;
      });

    this.transactionService.transactionUpdated
      .pipe(
        takeUntil(this.unsubscribe$),
        filter(() => this.isAuthenticated),
      )
      .subscribe(() => this.loadBalance());
  }

  ngOnDestroy(): void {
    if (this.loginSuccessActionSubscriber) {
      this.loginSuccessActionSubscriber.unsubscribe();
    }
  }

  private async loadBalance(): Promise<void> {
    try {
      const b = await firstValueFrom(this.accountService.getBalance());
      this.availableBalance = b.available;
      this.balance = b.balance;
    } catch (e) {
      console.error(e);
    }
  }

  private async loadPartners(): Promise<void> {
    try {
      this.partners = await firstValueFrom(
        this.brokerService.getPartnersList(),
      );
    } catch (e) {
      console.error(e);
    }
  }

  toggleMenu(): void {
    this.showMenu = !this.showMenu;
  }

  logout(): void {
    this.authenticationService.logout().subscribe();
  }

  /**
   * Open /profile page
   */
  openProfile(): void {
    this.router.navigate(['/profile']);
  }

  openHomePage(): void {
    if (this.isAuthenticated) {
      this.router.navigate(['/dashboard']);
    } else {
      this.router.navigate(['/login']);
    }
  }

  public async openApplyNow(): Promise<void> {
    await this.dealService.openApplyNow();
  }

  public async openReferFriend(): Promise<void> {
    const dialogRef = this.matDialog.open(ReferFriendComponent, {
      minHeight: '330px',
      panelClass: this.themesService.getDarkThemeClass(),
      backdropClass: this.themesService.getDarkThemeClass(),
    });
  }

  openHelp(): void {
    window.open('https://help.payfully.co/knowledge', '_blank');
  }
}
